import React, { useState, useEffect, useRef } from 'react';
import { initializeTransfer, listSubaccounts, getTransactionStatus } from '../apiHelper';


// Main PaystackBankTransferForm component for the Paystack Bank Transfer UI.
function PaystackBankTransferForm() {
    // State variables for form inputs
    const [email, setEmail] = useState('');
    const [amount, setAmount] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    // State for selected subaccount (still present, but optional for transfer)
    const [selectedSubaccount, setSelectedSubaccount] = useState('');

    // State variables for displaying Paystack response and UI feedback
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [virtualAccountDetails, setVirtualAccountDetails] = useState(null);
    const [transactionReference, setTransactionReference] = useState(null);
    // State for list of subaccounts fetched from backend
    const [subaccounts, setSubaccounts] = useState([]);
    const [subaccountsLoading, setSubaccountsLoading] = useState(true);
    const [subaccountsError, setSubaccountsError] = useState(null);

    // NEW STATE: To track the status of the transaction for polling
    const [transactionStatus, setTransactionStatus] = useState(null);
    // NEW REF: To store the interval ID for polling, so we can clear it
    const pollingIntervalRef = useRef(null);


    // Function to fetch subaccounts (reusable)
    const fetchSubaccounts = async () => {
        try {
            setSubaccountsLoading(true);
            setSubaccountsError(null);
            const data = await listSubaccounts();
            setSubaccounts(data);
            if (data.length > 0) {
                // Optionally pre-select the first subaccount or a default
                setSelectedSubaccount(data[0].recipient_code);
            }
        } catch (err) {
            let errorMessage = 'Failed to load subaccounts.';
            if (err.response && err.response.data && err.response.data.detail) {
                if (typeof err.response.data.detail === 'string') {
                    errorMessage = err.response.data.detail;
                } else if (Array.isArray(err.response.data.detail)) {
                    errorMessage = err.response.data.detail.map(d => d.msg || d.message || d).join(', ');
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            setSubaccountsError(errorMessage);
            console.error('Error fetching subaccounts:', err);
        } finally {
            setSubaccountsLoading(false);
        }
    };

    // Fetch subaccounts when the component mounts
    useEffect(() => {
        fetchSubaccounts();
    }, []); // Empty dependency array means this runs once on mount

    // NEW EFFECT: Polling for transaction status
    useEffect(() => {
        // Clear any existing interval when component unmounts or dependencies change
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []); // Empty dependency array means this cleanup runs once on unmount

    // NEW FUNCTION: Start polling for transaction status
    const startPollingTransactionStatus = (ref) => {
        // Clear any existing interval before starting a new one
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        pollingIntervalRef.current = setInterval(async () => {
            console.log(`Polling status for transaction: ${ref}`);
            try {
                const response = await getTransactionStatus(ref);
                if (response && response.status) {
                    setTransactionStatus(response.status);
                    if (response.status === 'successful' || response.status === 'failed') {
                        clearInterval(pollingIntervalRef.current); // Stop polling on final status
                        pollingIntervalRef.current = null;
                        console.log(`Polling stopped. Transaction status: ${response.status}`);
                    }
                }
            } catch (err) {
                console.error('Error polling transaction status:', err);
                // Optionally stop polling on error or implement retry logic
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
                setTransactionStatus('error'); // Indicate an error in polling
            }
        }, 5000); // Poll every 5 seconds
    };


    // Handles the form submission to initiate the bank transfer.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setVirtualAccountDetails(null);
        setTransactionReference(null);
        setTransactionStatus(null); // Reset transaction status on new submission

        try {
            const payload = {
                email,
                amount: parseInt(amount, 10) * 100, // Convert amount to kobo
                first_name: firstName || undefined,
                last_name: lastName || undefined,
                subaccount: selectedSubaccount || undefined, // Pass selected subaccount (will be undefined if none selected)
                metadata: {
                    source: "frontend_app",
                }
            };

            const data = await initializeTransfer(payload);

            setVirtualAccountDetails({
                accountNumber: data.virtual_account_number,
                accountName: data.virtual_account_name,
                bankName: data.virtual_account_bank,
            });
            setTransactionReference(data.transaction_reference);

            // NEW: Start polling for transaction status after successful initialization
            startPollingTransactionStatus(data.transaction_reference);

        } catch (err) {
            let errorMessage = 'An unexpected error occurred. Please try again.';
            if (err.response && err.response.data && err.response.data.detail) {
                if (typeof err.response.data.detail === 'string') {
                    errorMessage = err.response.data.detail;
                } else if (Array.isArray(err.response.data.detail)) {
                    errorMessage = err.response.data.detail.map(d => d.msg || d.message || d).join(', ');
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            setError(errorMessage);
            console.error('API Call Error in App.js:', err);
        } finally {
            setLoading(false);
        }
    };

    // NEW FUNCTION: Reset the form and UI to initial state
    const resetForm = () => {
        setEmail('');
        setAmount('');
        setFirstName('');
        setLastName('');
        setSelectedSubaccount(subaccounts.length > 0 ? subaccounts[0].recipient_code : '');
        setVirtualAccountDetails(null);
        setTransactionReference(null);
        setTransactionStatus(null); // Reset transaction status
        setError(null); // Clear any general errors
        // Ensure polling is stopped if it's active
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    return (
        <div className="bg-gray-100 min-h-screen flex flex-col items-center justify-center p-4 space-y-8">
            {/* Main Transfer Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Pay with Bank Transfer</h2>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6" role="alert">
                        <strong className="font-bold">Error!</strong>
                        <span className="block sm:inline ml-2">{error}</span>
                    </div>
                )}
                {subaccountsError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6" role="alert">
                        <strong className="font-bold">Subaccounts Error!</strong>
                        <span className="block sm:inline ml-2">{subaccountsError}</span>
                    </div>
                )}

                {/* Conditional Rendering based on transaction state */}
                {transactionStatus === 'successful' ? (
                    // NEW: Payment Receipt Section
                    <div className="text-center">
                        <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-2xl font-semibold text-gray-900">Payment Successful!</h3>
                        <p className="mt-1 text-lg text-gray-700">
                            Your order has been confirmed. Thank you for your payment.
                        </p>
                        <div className="bg-green-50 border border-green-200 p-6 rounded-lg mt-6 mb-6">
                            <p className="text-gray-800 text-xl font-bold mb-2">Amount Paid: NGN {parseFloat(amount).toLocaleString()}</p>
                            <p className="text-gray-600 text-lg">Transaction Ref: <span className="font-mono">{transactionReference}</span></p>
                            {virtualAccountDetails && (
                                <p className="text-gray-600 text-md">Paid via: {virtualAccountDetails.bankName} ({virtualAccountDetails.accountNumber})</p>
                            )}
                        </div>
                        <button
                            onClick={resetForm}
                            className="mt-8 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 shadow-md"
                        >
                            Start New Order
                        </button>
                    </div>
                ) : transactionStatus === 'failed' ? (
                    // NEW: Payment Failed Section
                    <div className="text-center">
                        <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-2xl font-semibold text-gray-900">Payment Failed</h3>
                        <p className="mt-1 text-lg text-gray-700">
                            Unfortunately, your payment could not be confirmed. Please try again.
                        </p>
                        <p className="text-sm text-gray-500 mt-4">
                            Transaction Ref: <span className="font-mono">{transactionReference}</span>
                        </p>
                        <button
                            onClick={resetForm}
                            className="mt-8 w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 shadow-md"
                        >
                            Try Again
                        </button>
                    </div>
                ) : virtualAccountDetails ? (
                    // Original DVA Display (while polling for status)
                    <div className="text-center">
                        <p className="text-lg text-gray-700 mb-4">
                            Please make a bank transfer of <span className="font-semibold text-green-600">NGN {parseFloat(amount).toLocaleString()}</span> to the account details below:
                        </p>
                        <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg mb-6">
                            <p className="text-gray-800 text-xl font-bold mb-2">{virtualAccountDetails.accountNumber}</p>
                            <p className="text-gray-600 text-lg">{virtualAccountDetails.accountName}</p>
                            <p className="text-gray-600 text-lg font-medium">{virtualAccountDetails.bankName}</p>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                            Your internal transaction reference: <span className="font-mono text-gray-700">{transactionReference}</span>
                        </p>
                        <p className="text-md text-gray-700">
                            Waiting for payment confirmation... Current status: <span className="font-semibold text-indigo-600">{transactionStatus || 'pending'}</span>
                        </p>
                        <button
                            onClick={resetForm}
                            className="mt-8 w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 shadow-md"
                        >
                            Cancel & Start New
                        </button>
                    </div>
                ) : (
                    // Initial Form
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out"
                                placeholder="customer@example.com"
                            />
                        </div>
                        <div>
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">Amount (NGN)</label>
                            <input
                                type="number"
                                id="amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                                min="1"
                                step="any"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out"
                                placeholder="e.g., 5000"
                            />
                        </div>
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name (Optional)</label>
                            <input
                                type="text"
                                id="firstName"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out"
                                placeholder="John"
                            />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name (Optional)</label>
                            <input
                                type="text"
                                id="lastName"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out"
                                placeholder="Doe"
                            />
                        </div>
                        <div>
                            <label htmlFor="subaccount" className="block text-sm font-medium text-gray-700 mb-1">
                                Settle To (Subaccount)
                            </label>
                            {subaccountsLoading ? (
                                <p className="text-gray-500">Loading subaccounts...</p>
                            ) : subaccounts.length === 0 ? (
                                <p className="text-gray-500">No active subaccounts found. Payments will settle to your main Paystack account.</p>
                            ) : (
                                <select
                                    id="subaccount"
                                    value={selectedSubaccount}
                                    onChange={(e) => setSelectedSubaccount(e.target.value)}
                                    // Make required=false if selecting a subaccount is optional
                                    required={false}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out bg-white"
                                >
                                    <option value="">-- Select an optional subaccount --</option> {/* Option for no subaccount */}
                                    {subaccounts.map((sa) => (
                                        <option key={sa.recipient_code} value={sa.recipient_code}>
                                            {sa.name} ({sa.bank_name} - {sa.account_number}) {sa.active ? '' : '(Unverified/Inactive)'}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-3 px-6 rounded-lg text-white font-semibold transition duration-300 ease-in-out transform shadow-md
                                   ${loading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:scale-105'}`}
                        >
                            {loading ? 'Processing...' : 'Initiate Bank Transfer'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );

}

export default PaystackBankTransferForm;
